import { FC, ReactNode, useMemo, useEffect, useRef, useState } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import {
  createStore,
  AppDispatch,
  clearAccountData,
  initializeAccountData,
  setAppInitialized,
} from 'application/store';
import { createLogger } from 'application/utils/LogUtils';

import useCurrentUser from 'domain/auth/useCurrentUser';

import LoadingScreen from '@zvuk-b2b/react-uikit/ui/LoadingScreen';

import { useAuthContext } from '../AuthProvider';
import { useApolloTypedClient } from './useApolloTypedClient';

import AuthProviderUtils from '../AuthProvider/utils';
import { useFeatureFlagsSync } from './useFeatureFlagsSync';

const USER_INITIALIZED_ERROR = /User matching query does not exist./;

const logger = createLogger('StoreProvider');

const StoreProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const client = useApolloTypedClient();
  const { userData, authToken, setAuthData } = useAuthContext();

  const [userRequest, userResult] = useCurrentUser();

  const [isReady, setIsReady] = useState(false);
  const [isMounted, setIsMounted] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const initializationRef = useRef({
    isInitialized: false,
    lastAuthToken: undefined as string | undefined,
    lastUserId: undefined as string | undefined,
    lastCompanyId: undefined as string | undefined,
  });

  const { store, persistor } = useMemo(() => createStore(client), [client]);

  useFeatureFlagsSync(client, store.dispatch as AppDispatch, logger, userData);

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (authToken) {
      AuthProviderUtils.setAuthTokenUpdateTimeout(userRequest);
    }
  }, [authToken]);

  useEffect(() => {
    if (userResult.data.user && !authToken) {
      logger.info('Clearing cached user data after logout', {
        userId: userResult.data.user.id,
      });

      try {
        client.cache.evict({
          id: `UserExtendPureType:${userResult.data.user.id}`,
        });

        logger.debug('User cache evicted successfully');
      } catch (error) {
        logger.warn('Failed to evict user cache:', error);
      }

      if (!userResult.data.user) {
        logger.debug('No user data in query result', {
          loading: userResult.loading,
          error: userResult.error?.message,
        });
      }
    }
  }, [userResult.data.user, authToken, client]);

  useEffect(() => {
    if (!store || !isMounted || isLoggingOut) {
      logger.debug('Skipping main effect', {
        hasStore: !!store,
        isMounted,
        isLoggingOut,
      });

      return;
    }

    const { isInitialized, lastAuthToken, lastUserId, lastCompanyId } = initializationRef.current;
    const dispatch = store.dispatch as AppDispatch;

    if (!authToken && lastAuthToken) {
      logger.info('🚪 LOGOUT DETECTED', {
        lastAuthToken: `${lastAuthToken.slice(0, 8)}...`,
        wasInitialized: isInitialized,
      });

      setIsLoggingOut(true);
      setIsReady(false);

      const cleanup = async () => {
        try {
          logger.info('🧹 Starting logout cleanup - clearing Apollo cache...');
          await client.clearStore();
          logger.info('✅ Apollo cache cleared');

          logger.info('🧹 Clearing Redux store...');
          dispatch(clearAccountData());
          logger.info('✅ Redux store cleared');

          initializationRef.current = {
            isInitialized: false,
            lastAuthToken: undefined,
            lastUserId: undefined,
            lastCompanyId: undefined,
          };

          logger.debug('Initialization ref reset');

          logger.info('🧹 Purging persist store...');
          if (isMounted) {
            await persistor.purge();
            logger.info('✅ Persist store purged');
          }

          logger.info('🎉 Logout cleanup completed successfully');
        } catch (error) {
          logger.error('💥 Error during logout cleanup:', error);
        } finally {
          if (isMounted) {
            logger.debug('Setting isLoggingOut = false');
            setIsLoggingOut(false);
          }
        }
      };

      cleanup();
      return;
    }

    if (!authToken && isMounted && !isLoggingOut) {
      logger.debug('👤 No auth token - setting ready state');
      dispatch(setAppInitialized());
      setIsReady(true);
      return;
    }

    if (authToken && !userData?.id) {
      logger.debug('⏳ Has token but no userData yet', {
        hasToken: !!authToken,
        hasUserData: !!userData,
        userId: userData?.id,
      });

      return;
    }

    if (authToken && userData?.id) {
      const currentCompanyId = userData?.mainCompany?.id;

      if (!currentCompanyId) {
        logger.debug('⚠️ User has no mainCompany', { userId: userData.id });
        return;
      }

      logger.debug('👤 Authenticated state', {
        userId: userData.id,
        companyId: currentCompanyId,
        isInitialized,
        lastAuthToken: `${lastAuthToken?.slice(0, 8)}...`,
        lastUserId,
        lastCompanyId,
      });

      const shouldInitialize =
        !isInitialized ||
        lastAuthToken !== authToken ||
        lastUserId !== userData.id ||
        lastCompanyId !== currentCompanyId;

      if (shouldInitialize) {
        logger.info('🎬 INITIALIZING STORE', {
          reason: {
            notInitialized: !isInitialized,
            tokenChanged: lastAuthToken !== authToken,
            userChanged: lastUserId !== userData.id,
            companyChanged: lastCompanyId !== currentCompanyId,
          },
          companyId: currentCompanyId,
        });

        if (userResult.data.user) {
          logger.info('Setting user data in auth context BEFORE initialization', {
            userId: userResult.data.user.id,
          });

          setAuthData({ userData: userResult.data.user, authToken });
        }

        dispatch(initializeAccountData(currentCompanyId))
          .unwrap()
          .then(() => {
            if (!isMounted) {
              logger.debug('Component unmounted during initialization - skipping');
              return;
            }

            logger.info('✅ Account data initialized successfully');

            dispatch(setAppInitialized());
            setIsReady(true);

            logger.info('🎉 Store fully initialized and ready');

            initializationRef.current = {
              isInitialized: true,
              lastAuthToken: authToken,
              lastUserId: userData.id,
              lastCompanyId: currentCompanyId,
            };

            logger.debug('Initialization ref updated');
          })
          .catch((error) => {
            if (!isMounted || isLoggingOut) {
              logger.debug('Initialization cancelled', { isMounted, isLoggingOut });
              return;
            }

            logger.error('💥 Failed to initialize store:', error);

            if (!isLoggingOut && USER_INITIALIZED_ERROR.test(error)) {
              logger.warn('User initialization error detected, clearing auth data');
              setAuthData({ authToken: undefined, userData: undefined });
            }

            setIsReady(false);
          });
      } else if (isInitialized && isMounted && !isLoggingOut) {
        logger.debug('✅ Store already initialized, setting ready', {
          lastCompanyId,
          currentCompanyId,
        });

        setIsReady(true);
      } else {
        logger.debug('⏸️ Not initializing - conditions not met', {
          isInitialized,
          isMounted,
          isLoggingOut,
        });
      }
    }
  }, [store, persistor, authToken, userData, isMounted, isLoggingOut, client]);

  const shouldShowLoading = isLoggingOut || (authToken && !userData) || (authToken && userData && !isReady);

  logger.debug('Render', {
    isLoggingOut,
    hasToken: !!authToken,
    hasUserData: !!userData,
    isReady,
    shouldShowLoading,
  });

  if (shouldShowLoading) {
    logger.debug('⏳ Rendering LoadingScreen');
    return <LoadingScreen />;
  }

  logger.debug('✅ Rendering ReduxProvider + children');

  return (
    <ReduxProvider store={store}>
      <PersistGate
        loading={null}
        persistor={persistor}>
        {children}
      </PersistGate>
    </ReduxProvider>
  );
};

export default StoreProvider;
